import { apiFetch } from "../api/client";
import type { EventContext } from "../context/event";
import type { Extended } from "./types";

/**
 * Writing settings back: `PUT /settings/fo` on apix.
 *
 * The server DEEP-MERGES what it receives (`updateFo`), which is why only the
 * changed keys are ever sent. Two people editing different settings therefore do
 * not overwrite one another — the last full-object write would have.
 *
 * ⚠️ A MERGE CANNOT REMOVE, which is why `unset` exists beside `extended`: a
 * list of dotted paths the server deletes AFTER merging. Without it "Reprendre
 * l'héritée" was inexpressible — an absent key is left alone by a merge, and a
 * null one is stored as null, which the cascade reads as a value rather than a
 * silence. Added to `updateFo` for exactly this.
 *
 * ⚠️ THE ROUTE STILL ENFORCES NO RIGHT. `useHandler` checks authentication and
 * domain membership, nothing more: any registered participant can rewrite an
 * event's configuration. Said again here because this file is where it stops
 * being theoretical.
 */
export async function saveSettings(
  event: EventContext,
  languageId: number,
  extended: Extended,
  unset: string[],
  signal?: AbortSignal,
  /** Voir `apiFetch` : le vidage au masquage doit survivre à la fermeture de l'onglet. */
  keepalive = false,
): Promise<void> {
  await apiFetch("/settings/fo", {
    api: "apix",
    method: "PUT",
    body: {
      eventId: event.eventId,
      domainId: event.domainId,
      languageId,
      extended,
      // Omitted when empty: an older API that does not know the field must not
      // receive it, and an empty list would be noise in every request.
      ...(unset.length > 0 ? { unset } : {}),
    },
    signal,
    keepalive,
  });
}
